import React, { useEffect, useRef, useState } from "react";
import "./styles/dashboard.css";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable'
import CommonHeader from "./components/CommonHeader";
import { hasPermission } from "./utils/permissionUtils";

/** Parses API datetime like "2025-08-29 08:00:00" and returns time only, e.g. "08:00 AM". */
function formatFlightTimeAmPm(flightTime) {
  if (flightTime == null || flightTime === "") return "";
  const str = String(flightTime).trim();
  const match = str.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return "";
  let h = parseInt(match[1], 10);
  const m = match[2];
  if (Number.isNaN(h)) return "";
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, "0")}:${m} ${period}`;
}

/** Appends formatted flight time in brackets after the drop point label when present. */
function dropPointWithFlightTime(dropPointName, flightTime) {
  const t = formatFlightTimeAmPm(flightTime);
  if (!t) return dropPointName ?? "";
  return `${dropPointName ?? ""} (${t})`;
}

// Same as dropPointWithFlightTime; legacy name for any JSX still calling it (avoids no-undef).
// eslint-disable-next-line no-unused-vars -- defined for compatibility; UI uses drop column + plain pax_name in modal
function paxNameWithFlightTime(paxName, flightTime) {
  return dropPointWithFlightTime(paxName, flightTime);
}

/** Normalize a URL token (add https for www.). */
function normalizePickupUrlToken(token) {
  const t = String(token || "").trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  if (/^www\./i.test(t)) return `https://${t}`;
  return null;
}

/** True when details are a single line that is only a URL (opens directly). */
function isSubPickupDetailsLinkOnly(details) {
  const s = String(details ?? "").trim();
  if (!s) return false;
  const lines = s.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length !== 1) return false;
  return !!normalizePickupUrlToken(lines[0]);
}

const URL_IN_SUB_PICKUP_DETAILS =
  /https?:\/\/[^\s<>"{}|\\^`[\]]+|www\.[^\s<>"{}|\\^`[\]]+/gi;

function findFirstUrlInSubPickupDetails(details) {
  const s = String(details ?? "");
  URL_IN_SUB_PICKUP_DETAILS.lastIndex = 0;
  const m = URL_IN_SUB_PICKUP_DETAILS.exec(s);
  if (!m) return null;
  return normalizePickupUrlToken(m[0]);
}

function activityLogIconClass(activityType) {
  switch (String(activityType || "").toUpperCase()) {
    case "CREATE":
      return "bi-plus-circle text-success";
    case "EDIT":
      return "bi-pencil-square text-primary";
    case "DELETE":
      return "bi-trash text-danger";
    case "FOLLOW_UP":
      return "bi-flag text-warning";
    case "REACH_INSIDE_BUS":
      return "bi-bus-front text-info";
    case "DRIVER_BUS":
      return "bi-person-badge text-secondary";
    default:
      return "bi-journal-text text-muted";
  }
}

function activityLogTypeLabel(activityType) {
  switch (String(activityType || "").toUpperCase()) {
    case "CREATE":
      return "Created";
    case "EDIT":
      return "Edited";
    case "DELETE":
      return "Cancelled";
    case "FOLLOW_UP":
      return "Follow-up";
    case "REACH_INSIDE_BUS":
      return "Reach inside bus";
    case "DRIVER_BUS":
      return "Driver / bus";
    default:
      return activityType || "Activity";
  }
}

/** API may return object or JSON string for `changes`. */
function normalizeActivityChanges(changes) {
  if (changes == null) return null;
  if (typeof changes === "object" && !Array.isArray(changes)) return changes;
  if (typeof changes === "string") {
    try {
      const parsed = JSON.parse(changes);
      return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
        ? parsed
        : null;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Each change value: { old, new, old_label?, new_label? } — labels are preferred for display;
 * raw id shown in muted text when it differs from the label.
 */
function renderActivityChangeCell(pair, side) {
  const isOld = side === "old";
  const rawKey = isOld ? "old" : "new";
  const labelKey = isOld ? "old_label" : "new_label";

  if (!pair || typeof pair !== "object") return "—";

  const raw = pair[rawKey];
  const label = pair[labelKey];

  const rawStr =
    raw != null && String(raw).trim() !== "" ? String(raw) : null;
  const labelStr =
    label != null && String(label).trim() !== "" ? String(label).trim() : null;

  if (labelStr && rawStr && labelStr !== rawStr) {
    return (
      <>
        <span className="d-block">{labelStr}</span>
        <span className="text-muted small">{rawStr}</span>
      </>
    );
  }
  if (labelStr) return labelStr;
  if (rawStr) return rawStr;
  return "—";
}

const Bookings = () => {

  const doc = new jsPDF();

  const navigate = useNavigate();
  const FILTERS_STORAGE_KEY = "bookings_filters_v1";
  const initialFilters = (() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = sessionStorage.getItem(FILTERS_STORAGE_KEY);
      return raw ? JSON.parse(raw) || {} : {};
    } catch {
      return {};
    }
  })();
  const hasRestoredFilters = (() => {
    if (!initialFilters || typeof initialFilters !== "object") return false;
    return (
      !!initialFilters.pickupDate ||
      !!initialFilters.agencyFk ||
      !!initialFilters.timeId ||
      !!initialFilters.pickupPointId ||
      !!initialFilters.followUpFk ||
      !!initialFilters.searchPaxName ||
      !!initialFilters.searchPaxContact
    );
  })();

  const [bookings, setBookings] = useState(null);

  const [times, setTimes] = useState(null);

  const [pickups, setPickups] = useState(null);

  const [followupStatus, setFollowupStatus] = useState(null);

  const [agencies, setAgencies] = useState(null);

  const [activeTab, setActiveTab] = useState(
    initialFilters.activeTab === "search" ? "search" : "default"
  ); // "default" | "search"

  const [searchPaxName, setSearchPaxName] = useState(initialFilters.searchPaxName || "");

  const [searchPaxContact, setSearchPaxContact] = useState(initialFilters.searchPaxContact || "");
  const [agencyFk, setAgencyFk] = useState(initialFilters.agencyFk || "");
  const [pickupDate, setPickupDate] = useState(initialFilters.pickupDate || "");
  const [timeId, setTimeId] = useState(initialFilters.timeId || "");
  const [pickupPointId, setPickupPointId] = useState(initialFilters.pickupPointId || "");
  const [followUpFk, setFollowUpFk] = useState(initialFilters.followUpFk || "");

  const [dele, setDele] = useState(false);
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [selectedFollowupStatus, setSelectedFollowupStatus] = useState('');
  const [selectedBookingInfo, setSelectedBookingInfo] = useState(null);
  const [notifyAgent, setNotifyAgent] = useState(true);

  /** { details: string, url: string | null } when mixed text + link popup is open */
  const [subPickupDetailsModal, setSubPickupDetailsModal] = useState(null);

  /** Activity log modal: entries null until loaded */
  const [activityLogModal, setActivityLogModal] = useState(null);
  const [activityLogExpandedIds, setActivityLogExpandedIds] = useState({});
  const hasAutoSubmittedRef = useRef(false);

  let slno  = 0;

  function handlePDF() {
    if (!bookings || bookings.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Prepare data for PDF with row styling
    const tableData = bookings.map((booking, index) => {
      const rowData = [
        index + 1,
        booking.pax_name,
        booking.pax_contact_no,
        dropPointWithFlightTime(booking.drop_point_name, booking.flight_time),
        booking.pickup_point_name,
        booking.sub_pickup,
        booking.agency_name,
        booking.remarks
      ];

      // Apply row styling based on follow_up_status_color_code
      const rowStyle = {};
      if (booking.follow_up_status_color_code) {
        rowStyle.fillColor = booking.follow_up_status_color_code;
        rowStyle.textColor = [0, 0, 0]; // Black text for colored backgrounds
      }

      return {
        data: rowData,
        styles: rowStyle
      };
    });

    // Create PDF table
    doc.autoTable({
      head: [['Sl No', 'Pax Name', 'Contact No', 'Drop Point', 'Pickup Point', 'Sub Pickup', 'Agency Name', 'Remarks']],
      body: tableData.map(row => row.data),
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      didParseCell: function(data) {
        // Apply custom row styling
        const rowIndex = data.row.index;
        if (rowIndex > 0 && tableData[rowIndex - 1] && tableData[rowIndex - 1].styles.fillColor) {
          data.cell.styles.fillColor = tableData[rowIndex - 1].styles.fillColor;
          data.cell.styles.textColor = tableData[rowIndex - 1].styles.textColor;
        }
        
        // Apply reach_inside_bus highlighting to slno column (first column)
        if (data.column.index === 0 && rowIndex > 0) {
          const booking = bookings[rowIndex - 1];
          if (booking && booking.reach_inside_bus === '1') {
            data.cell.styles.fillColor = [57, 255, 20]; // #39FF14 in RGB
            data.cell.styles.textColor = [0, 0, 0]; // Black text for better contrast
          }
        }
      }
    });

    doc.save('Booking_Report.pdf');
  }

  function handleSubmit(overrides) {
    const effectivePickupPointId =
      overrides != null &&
      typeof overrides === "object" &&
      Object.prototype.hasOwnProperty.call(overrides, "pickupPointId")
        ? overrides.pickupPointId
        : pickupPointId;

    const data = new URLSearchParams();

    if (activeTab === "search") {
      const paxName = (searchPaxName || "").trim();
      const paxContact = (searchPaxContact || "").trim();
      if (!paxName && !paxContact) {
        toast.error("Passenger name or mobile number is required for search.");
        return;
      }
      data.append("search_tab", "1");
      if (paxName) data.append("pax_name", paxName);
      if (paxContact) data.append("pax_contact_no", paxContact);
    } else {
      const pickupDateValue = (pickupDate || "").trim();
      if (!pickupDateValue) {
        toast.error("Pickup date is required.");
        return;
      }
      data.append("pickup_date", pickupDateValue);
      if (agencyFk) data.append("agency_fk", agencyFk);
      if (timeId) data.append("time_id", timeId);
      if (effectivePickupPointId) data.append("pickup_point_id", effectivePickupPointId);
      if (followUpFk) data.append("follow_up_status_fk", followUpFk);
    }

    fetch(`${process.env.REACT_APP_API_URL}/booking/getlist`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: data,
    }).then(async (res) => {
      let resp = await res.json();
      console.log(resp);

      if (resp.status === 1 && resp.message === "OK") {
        if (!resp.list || resp.list.length === 0) {
          toast.error("No data found");
          setBookings([]);
        } else {
          setBookings(resp.list);
        }
      } else {
        toast.error(resp.message || "Request failed");
      }
    });







  }

  function handleFollowupStatusUpdate() {
    if (!selectedFollowupStatus) {
      toast.error('Please select a follow-up status');
      return;
    }

    const data = new URLSearchParams();
    data.append('booking_id', selectedBookingId);
    data.append('follow_up_status_fk', selectedFollowupStatus);
    data.append('notify_agent_via_whatsapp', notifyAgent ? '1' : '0');
    
    fetch(`${process.env.REACT_APP_API_URL}/booking/updatefollowupstatus`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: data,
    }).then(async (res) => {
      let resp = await res.json();
      console.log(resp);

      if (resp.status === 1) {
        toast.success("Follow-up status updated successfully");
        setShowFollowupModal(false);
        setSelectedBookingId(null);
        setSelectedFollowupStatus('');
        setSelectedBookingInfo(null);
        setNotifyAgent(true); // Reset to default
        setDele(true); // Refresh the bookings list
      } else {
        toast.error(resp.message);
      }
    });
  }

  function openActivityLog(bookingId) {
    setActivityLogModal({
      bookingId,
      bookingIdDisplay: String(bookingId),
      entries: null,
      loading: true,
      error: null,
    });
    setActivityLogExpandedIds({});

    const data = new URLSearchParams();
    data.append("booking_id", bookingId);

    fetch(`${process.env.REACT_APP_API_URL}/booking/getactivitylog`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: data,
    })
      .then(async (res) => {
        let resp = await res.json();
        if (resp.status === 1 && resp.message === "OK") {
          setActivityLogModal({
            bookingId,
            bookingIdDisplay: resp.booking_id != null ? String(resp.booking_id) : String(bookingId),
            entries: Array.isArray(resp.list) ? resp.list : [],
            loading: false,
            error: null,
          });
        } else {
          setActivityLogModal({
            bookingId,
            bookingIdDisplay: String(bookingId),
            entries: null,
            loading: false,
            error: resp.message || "Failed to load activity log",
          });
        }
      })
      .catch(() => {
        setActivityLogModal({
          bookingId,
          bookingIdDisplay: String(bookingId),
          entries: null,
          loading: false,
          error: "Network error",
        });
      });
  }

  function closeActivityLog() {
    setActivityLogModal(null);
    setActivityLogExpandedIds({});
  }

  function toggleActivityLogExpand(logId) {
    setActivityLogExpandedIds((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  }

  function handleReachInsideBusUpdate(bookingId, currentStatus) {
    const newStatus = currentStatus === '1' ? '0' : '1';
    
    const data = new URLSearchParams();
    data.append('booking_id', bookingId);
    data.append('reach_inside_bus', newStatus);
    
    fetch(`${process.env.REACT_APP_API_URL}/booking/updatereachinsidebus`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: data,
    }).then(async (res) => {
      let resp = await res.json();
      console.log(resp);

      if (resp.status === 1) {
        toast.success("Reach inside bus status updated successfully");
        setDele(true); // Refresh the bookings list
      } else {
        toast.error(resp.message);
      }
    });
  }

  function handleSubmit_To_whatsapp() {
    if (activeTab !== "default") return;
    const data = new URLSearchParams();
    if (!pickupDate) {
      toast.error("Pickup date is required.");
      return;
    }
    data.append("pickup_date", pickupDate);
    if (timeId) data.append("time_id", timeId);
    if (pickupPointId) data.append("pickup_point_id", pickupPointId);
    if (followUpFk) data.append("follow_up_status_fk", followUpFk);
    if (agencyFk) data.append("agency_fk", agencyFk);

    fetch(`${process.env.REACT_APP_API_URL}/booking/send_whatsapp`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: data,
    }).then(async (res) => {
      let resp = await res.json();
      console.log(resp);

      if (resp.message === "OK") {

        alert('Message sent');

      } else {
        toast.error(resp.message);
      }
    });

  }

  function formatDate(date) {
    var day = date.getDate();
    var month = date.getMonth() + 1; // Months are zero based
    var year = date.getFullYear();

    // Add leading zeros to day and month if necessary
    if(day < 10) {
        day = '0' + day;
    }
    if(month < 10) {
        month = '0' + month;
    }

    return year + '-' + month + '-' + day;
}

  useEffect(() => {
    sessionStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({
        activeTab,
        searchPaxName,
        searchPaxContact,
        agencyFk,
        pickupDate,
        timeId,
        pickupPointId,
        followUpFk,
      })
    );
  }, [activeTab, searchPaxName, searchPaxContact, agencyFk, pickupDate, timeId, pickupPointId, followUpFk]);

  useEffect(() => {
    if (hasAutoSubmittedRef.current) return;
    if (!hasRestoredFilters) return;

    if (activeTab === "default" && pickupDate) {
      hasAutoSubmittedRef.current = true;
      handleSubmit();
      return;
    }

    if (activeTab === "search" && ((searchPaxName || "").trim() || (searchPaxContact || "").trim())) {
      hasAutoSubmittedRef.current = true;
      handleSubmit();
    }
  }, [hasRestoredFilters, activeTab, pickupDate, searchPaxName, searchPaxContact]);

  useEffect(() => {

    fetch(`${process.env.REACT_APP_API_URL}/suggests/getbustimings`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: [],
    }).then(async (res) => {
      let resp = await res.json();
      if (resp.message === "OK") {

        setTimes(resp.list);
        console.log(resp);

      } else {

        toast.error(resp.message);

      }

      
    });


    fetch(`${process.env.REACT_APP_API_URL}/suggests/getpickuplocations`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: [],
    }).then(async (res) => {
      let resp = await res.json();
      if (resp.message === "OK") {

        setPickups(resp.list);
        console.log(resp);

      } else {

        toast.error(resp.message);

      }

      
    });

    fetch(`${process.env.REACT_APP_API_URL}/suggests/getfollowupstatus`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: [],
    }).then(async (res) => {
      let resp = await res.json();
      if (resp.message === "OK") {

        setFollowupStatus(resp.list);
        console.log(resp);

      } else {

        toast.error(resp.message);

      }

      
    });

    fetch(`${process.env.REACT_APP_API_URL}/suggests/getagencies`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: [],
    }).then(async (res) => {
      let resp = await res.json();
      if (resp.list) {
        setAgencies(resp.list);
      }
    });

    if (dele) {
      handleSubmit();
      setDele(false);
    } else {
      // Keep selected date if restored from session; otherwise initialize to today.
      setPickupDate((prev) => prev || formatDate(new Date()));
    }


   
  }, [dele])
  

  
   

  

  return (
    <>
      <div class="d-flex flex-column flex-lg-row h-lg-full bg-surface-secondary">
        <div class="h-screen flex-grow-1 overflow-y-lg-auto">
          <CommonHeader />

          <main class="py-6 bg-surface-secondary">
            <div class="container-fluid">
              <div class="card shadow border-0 mb-7">
                {/* Tabs: Default (filters) vs Search */}
                <ul class="nav nav-tabs px-3 pt-3" style={{ borderBottom: "1px solid #dee2e6" }}>
                  <li class="nav-item">
                    <button
                      type="button"
                      class={`nav-link ${activeTab === "default" ? "active" : ""}`}
                      onClick={() => setActiveTab("default")}
                      style={{
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        color: activeTab === "default" ? "#0d6efd" : "#6c757d",
                        fontWeight: activeTab === "default" ? "600" : "normal",
                      }}
                    >
                      Filters
                    </button>
                  </li>
                  <li class="nav-item">
                    <button
                      type="button"
                      class={`nav-link ${activeTab === "search" ? "active" : ""}`}
                      onClick={() => setActiveTab("search")}
                      style={{
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        color: activeTab === "search" ? "#0d6efd" : "#6c757d",
                        fontWeight: activeTab === "search" ? "600" : "normal",
                      }}
                    >
                      Search
                    </button>
                  </li>
                </ul>

                {activeTab === "default" ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "left",
                      gap: "2rem",
                      flexWrap: "wrap",
                      padding: "32px 0 28px",
                      paddingLeft: "24px",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label style={{ color: "black", marginBottom: "0.3rem", fontSize: "0.875rem" }}>Agency:</label>
                      <select
                        id="agency"
                        class="form-select form-select-sm"
                        style={{ minWidth: "140px" }}
                        value={agencyFk}
                        onChange={(e) => setAgencyFk(e.target.value)}
                      >
                        <option value="">All...</option>
                        {agencies && agencies.map((ag) => (
                          <option key={ag.agency_id || ag.agency_fk} value={ag.agency_id || ag.agency_fk}>
                            {ag.agency_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label style={{ color: "black", marginBottom: "0.3rem", fontSize: "0.875rem" }}>Pickup Date:</label>
                      <input
                        className="form-control form-control-sm"
                        type="date"
                        id="date"
                        style={{ minWidth: "140px" }}
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                      />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label style={{ color: "black", marginBottom: "0.3rem", fontSize: "0.875rem" }}>Bus Time:</label>
                      <select
                        id="time"
                        class="form-select form-select-sm"
                        style={{ minWidth: "140px" }}
                        value={timeId}
                        onChange={(e) => setTimeId(e.target.value)}
                      >
                        <option value="">Choose...</option>
                        {times && times.map((time) => (
                          <option key={time.time_id} value={time.time_id}>{time.time_name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label style={{ color: "black", marginBottom: "0.3rem", fontSize: "0.875rem" }}>Pickup:</label>
                      <select
                        id="pickup"
                        class="form-select form-select-sm"
                        style={{ minWidth: "140px" }}
                        value={pickupPointId}
                        onChange={(e) => setPickupPointId(e.target.value)}
                      >
                        <option value="">All...</option>
                        {pickups && pickups.map((p) => (
                          <option key={p.pickup_point_id} value={p.pickup_point_id}>{p.pickup_point_name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label style={{ color: "black", marginBottom: "0.3rem", fontSize: "0.875rem" }}>Follow-up Status:</label>
                      <select
                        id="followup_status"
                        class="form-select form-select-sm"
                        style={{ minWidth: "140px" }}
                        value={followUpFk}
                        onChange={(e) => setFollowUpFk(e.target.value)}
                      >
                        <option value="">All...</option>
                        {followupStatus && followupStatus.map((status) => (
                          <option key={status.follow_up_status_id} value={status.follow_up_status_id}>{status.follow_up_status_name}</option>
                        ))}
                      </select>
                    </div>
                    <button
                      className="btn btn-primary btn-sm shadow-none"
                      onClick={handleSubmit}
                      style={{ height: "31px", marginBottom: "1px" }}
                    >
                      Submit
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "left",
                      gap: "2rem",
                      flexWrap: "wrap",
                      padding: "32px 0 28px",
                      paddingLeft: "24px",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label style={{ color: "black", marginBottom: "0.3rem", fontSize: "0.875rem" }}>Passenger name:</label>
                      <input
                        className="form-control form-control-sm"
                        type="text"
                        placeholder="Partial match"
                        value={searchPaxName}
                        onChange={(e) => setSearchPaxName(e.target.value)}
                        style={{ minWidth: "180px" }}
                      />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label style={{ color: "black", marginBottom: "0.3rem", fontSize: "0.875rem" }}>Mobile number:</label>
                      <input
                        className="form-control form-control-sm"
                        type="text"
                        placeholder="Partial match"
                        value={searchPaxContact}
                        onChange={(e) => setSearchPaxContact(e.target.value)}
                        style={{ minWidth: "180px" }}
                      />
                    </div>
                    <button
                      className="btn btn-primary btn-sm shadow-none"
                      onClick={handleSubmit}
                      style={{ height: "31px", marginBottom: "1px" }}
                    >
                      Search
                    </button>
                  </div>
                )}

                {/* Quick Pickup Location Buttons - only on default tab */}
                {activeTab === "default" && pickups && pickups.length > 0 && (
                  <div class="mt-3 mb-4">
                    <div class="d-flex align-items-center mb-2">
                      <span style={{ color: "black", fontWeight: "500" }}>Quick Access:</span>
                    </div>
                    <div class="d-flex flex-wrap gap-2">
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => {
                          setPickupPointId("");
                          handleSubmit({ pickupPointId: "" });
                        }}
                      >
                        All Pickups
                      </button>
                      {pickups.map((pickup) => (
                        <button
                          key={pickup.pickup_point_id}
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => {
                            const id = String(pickup.pickup_point_id);
                            setPickupPointId(id);
                            handleSubmit({ pickupPointId: id });
                          }}
                          title={`View bookings for ${pickup.pickup_point_name}`}
                        >
                          {pickup.pickup_point_name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div class="table-responsive">
                  <table class="table table-hover table-nowrap" id="myTable">
                    <thead class="thead-light">
                      <tr>
                        <th>Sl No</th>
                        <th scope="col" class="text-center" title="Follow-up Status"></th>
                        <th scope="col">Pax Name</th>
                        <th scope="col">Contact No</th>
                        <th scope="col">Drop Point</th>
                        <th scope="col">Pickup Point</th>
                        <th scope="col">Sub Pickup</th>
                        <th scope="col">Agency Name</th>
                        {activeTab === "search" && (
                          <>
                            <th scope="col">Pickup Date</th>
                            <th scope="col">Time</th>
                          </>
                        )}
                        <th>Remarks</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                      
                      bookings ? (
                        
                        bookings.map((booking, user) => {
                          slno++;
                          const dropPointDisplay = dropPointWithFlightTime(
                            booking.drop_point_name,
                            booking.flight_time
                          );
                          var telno = "tel:"+booking.pax_contact_no;
                          var user_details = booking.added_user + " on " + booking.added_on + ", "+ booking.time_name;
                          
                          // Add row highlighting based on follow_up_status_color_code
                          const rowStyle = booking.follow_up_status_color_code ? {
                            backgroundColor: booking.follow_up_status_color_code
                          } : {};
                          
                          return (
                            <tr style={rowStyle}>
                              <td 
                                style={{
                                  backgroundColor: booking.reach_inside_bus === '1' ? '#39FF14' : 'transparent',
                                  cursor: 'pointer'
                                }}
                                onDoubleClick={() => handleReachInsideBusUpdate(booking.booking_id, booking.reach_inside_bus)}
                                title="Double-click to toggle reach inside bus status"
                              >
                                {slno}
                              </td>
                              <td class="text-center">
                                <button
                                  onClick={() => {
                                    setSelectedBookingId(booking.booking_id);
                                    setSelectedBookingInfo({
                                      pax_name: booking.pax_name,
                                      pax_contact_no: booking.pax_contact_no,
                                      agency_name: booking.agency_name,
                                      chat_name: booking.whatsapp_chat_name
                                    });
                                    setSelectedFollowupStatus(booking.follow_up_status_id || '');
                                    if (booking.follow_up_status_id && followupStatus) {
                                      const currentStatus = followupStatus.find(status => status.follow_up_status_id === booking.follow_up_status_id);
                                      if (currentStatus) {
                                        setNotifyAgent(currentStatus.notify_whatsapp === '1');
                                      }
                                    }
                                    setShowFollowupModal(true);
                                  }}
                                  type="button"
                                  class="btn btn-sm btn-square btn-neutral shadow-none"
                                  title="Update Follow-up Status"
                                >
                                  <i class="bi bi-flag"></i>
                                </button>
                              </td>
                              <td>{booking.pax_name}</td>
                              <td>
                                <a href={telno}>{booking.pax_contact_no}</a>
                              </td>
                              <td>{dropPointDisplay}</td>
                              <td>
                                {booking.pickup_point_name}
                              </td>
                              <td>
                                {(() => {
                                  const detailsRaw =
                                    booking.sub_pickup_location_details;
                                  const hasDetails =
                                    detailsRaw != null &&
                                    String(detailsRaw).trim() !== "";
                                  const label =
                                    booking.sub_pickup != null &&
                                    String(booking.sub_pickup).trim() !== ""
                                      ? booking.sub_pickup
                                      : "Map link";

                                  if (!hasDetails) {
                                    return booking.sub_pickup ?? "";
                                  }

                                  const detailsStr = String(detailsRaw);
                                  if (isSubPickupDetailsLinkOnly(detailsStr)) {
                                    const line = detailsStr
                                      .split(/\r?\n/)
                                      .map((l) => l.trim())
                                      .filter(Boolean)[0];
                                    const href = normalizePickupUrlToken(line);
                                    return (
                                      <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        {label}
                                      </a>
                                    );
                                  }

                                  const url =
                                    findFirstUrlInSubPickupDetails(detailsStr);
                                  return (
                                    <button
                                      type="button"
                                      class="btn btn-link p-0 align-baseline text-start shadow-none"
                                      style={{
                                        textDecoration: "underline",
                                        maxWidth: "100%",
                                      }}
                                      onClick={() =>
                                        setSubPickupDetailsModal({
                                          details: detailsStr,
                                          url,
                                        })
                                      }
                                    >
                                      {label}
                                    </button>
                                  );
                                })()}
                              </td>
                              <td>
                                {booking.agency_name}
                              </td>
                              {activeTab === "search" && (
                                <>
                                  <td>{booking.pickup_date}</td>
                                  <td>{booking.time_name}</td>
                                </>
                              )}
                              <td title={user_details}>{booking.remarks}</td>
                              <td class="text-end">
                                <button
                                  type="button"
                                  class="btn btn-sm btn-square btn-neutral shadow-none"
                                  title="Activity log"
                                  style={{ marginRight: "0.5rem" }}
                                  onClick={() => openActivityLog(booking.booking_id)}
                                >
                                  <i class="bi bi-clock-history"></i>
                                </button>
                                <button
                                  onClick={() => {
                                    navigate(`/bookings/edit/${booking.booking_id}`);

                                    localStorage.setItem(
                                      "pax_name",
                                      `${booking.pax_name}`
                                    );

                                    localStorage.setItem(
                                      "pax_number",
                                      `${booking.pax_contact_no}`
                                    );
                                    localStorage.setItem(
                                      "pickup_date",
                                      `${booking.pickup_date}`
                                    );
                                    localStorage.setItem(
                                      "pickup_time",
                                      `${booking.pickup_time}`
                                    );
                                    localStorage.setItem("flight_time", `${booking.flight_time}`);
                                    localStorage.setItem("remarks", `${booking.remarks}`);
                                    localStorage.setItem("agency_name", `${booking.agency_name}`);
                                    localStorage.setItem("pickup_point_name", `${booking.pickup_point_name}`);
                                    localStorage.setItem("sub_pickup", `${booking.sub_pickup}`);
                                    localStorage.setItem(
                                      "sub_pickup_location_details",
                                      booking.sub_pickup_location_details != null
                                        ? `${booking.sub_pickup_location_details}`
                                        : ""
                                    );

                                    localStorage.setItem("drop_point_name", `${booking.drop_point_name}`);
                                    localStorage.setItem("bus_time_id", `${booking.bus_time_id}`);

                                    // Audit/caption fields for Edit Booking screen
                                    localStorage.setItem(
                                      "added_user",
                                      booking.added_user ? `${booking.added_user}` : ""
                                    );
                                    localStorage.setItem(
                                      "last_modified_user",
                                      booking.last_modified_user ? `${booking.last_modified_user}` : ""
                                    );
                                    localStorage.setItem(
                                      "added_on_formatted",
                                      booking.added_on_formatted
                                        ? `${booking.added_on_formatted}`
                                        : booking.added_on
                                          ? `${booking.added_on}`
                                          : ""
                                    );
                                    localStorage.setItem(
                                      "last_modified_on_formatted",
                                      booking.last_modified_on_formatted
                                        ? `${booking.last_modified_on_formatted}`
                                        : booking.last_modified_on
                                          ? `${booking.last_modified_on}`
                                          : ""
                                    );
                                  }}
                                  class="btn btn-sm btn-neutral"
                                  style={{ marginRight: "0.5rem" }}
                                >
                                  Edit
                                </button>
                                {hasPermission('bookings/delete') && (
                                  <button
                                    onClick={()=>{
                                      if(window.confirm('Delete?')===false)
                                      {
                                        return false;
                                      }
                                      const data = new URLSearchParams();

                                      data.append('booking_id', booking.booking_id);
                                      
                                      fetch(`${process.env.REACT_APP_API_URL}/booking/deletebooking`, {
                                        method: "post",
                                        headers: {
                                          Authorization: localStorage.getItem("token"),
                                        },
                                        body: data,
                                      }).then(async (res) => {
                                        let resp = await res.json();
                                        console.log(resp);
                                  
                                        if (resp.message === "Deleted") {

                                          setDele(true);
                                  
                                          toast.success("Booking deleted")
                                  
                                        } else {
                                          toast.error(resp.message);
                                        }
                                      });

                                    }}
                                    type="button"
                                    class="btn btn-sm btn-square btn-neutral shadow-none"
                                    style={{ marginRight: "0.5rem" }}
                                  >
                                    <i class="bi bi-trash"></i>
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        
                        <tr>
                          <td colSpan={activeTab === "search" ? 12 : 10}>
                            {activeTab === "default"
                              ? "Select pickup date and click Submit to see bookings."
                              : "Enter passenger name and/or mobile number and click Search."}
                          </td>
                        </tr>
                        
                      )}
                    </tbody>
                  </table>
                </div>
                <div class="card-footer border-0 py-5">
                  {hasPermission('bookings/add') && (
                    <button
                      class="btn d-inline-flex btn-sm btn-primary mx-1"
                      onClick={() => {
                        navigate("/bookings/add");
                      }}
                    >
                      <span class=" pe-2">
                        <i class="bi bi-plus"></i>
                      </span>
                      <span>Add Booking</span>
                    </button>
                  )}

                  {hasPermission('bookings/topagents') && (
                    <button
                      class="btn d-inline-flex btn-sm btn-primary mx-1"
                      onClick={() => {
                        navigate("/bookings/topagents");
                      }}
                    >
                      <span class="pe-2">
                        <i class="bi bi-graph-up"></i>
                      </span>
                      <span>Top Agents</span>
                    </button>
                  )}

                  <button
                        class="btn d-inline-flex btn-sm btn-primary mx-1"
                        onClick={handlePDF}
                      >
                        <span class=" pe-2">
                          <i class="bi bi-download"></i>
                        </span>
                        <span>PDF</span>
                      </button>

                      <button
                        class="btn d-inline-flex btn-sm btn-primary mx-1"
                        onClick={handleSubmit_To_whatsapp}
                        disabled={activeTab === "search"}
                        title={activeTab === "search" ? "Available only when using Filters tab" : "Send to WhatsApp"}
                      >
                        <span class=" pe-2">
                          <i class="bi bi-download"></i>
                        </span>
                        <span>Send to Whatsapp</span>
                      </button>

                </div>

                {/* Follow-up Status Legend */}
                {followupStatus && followupStatus.length > 0 && (
                  <div class="mt-4 pt-3 border-top">
                    <h6 class="mb-3">Follow-up Status Legend:</h6>
                    <div class="d-flex flex-wrap gap-3">
                      {followupStatus.map((status) => (
                        <div 
                          key={status.follow_up_status_id} 
                          class="d-flex align-items-center"
                          style={{
                            padding: "0.5rem 1rem",
                            borderRadius: "0.375rem",
                            border: "1px solid #dee2e6",
                            backgroundColor: status.follow_up_status_color_code || "transparent"
                          }}
                        >
                          <div 
                            class="me-2"
                            style={{
                              width: "12px",
                              height: "12px",
                              borderRadius: "50%",
                              backgroundColor: status.follow_up_status_color_code || "#6c757d",
                              border: status.follow_up_status_color_code ? "none" : "1px solid #6c757d"
                            }}
                          ></div>
                          <span style={{ 
                            color: status.follow_up_status_color_code ? "#000" : "#6c757d",
                            fontSize: "0.875rem"
                          }}>
                            {status.follow_up_status_name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Sub pickup location details (text + link) */}
      {subPickupDetailsModal && (
        <div
          class="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          onClick={() => setSubPickupDetailsModal(null)}
        >
          <div
            class="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Sub pickup location</h5>
                <button
                  type="button"
                  class="btn-close"
                  aria-label="Close"
                  onClick={() => setSubPickupDetailsModal(null)}
                />
              </div>
              <div class="modal-body">
                <pre
                  class="mb-3 p-2 bg-light rounded border small"
                  style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {subPickupDetailsModal.details}
                </pre>
                {subPickupDetailsModal.url ? (
                  <a
                    href={subPickupDetailsModal.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="d-inline-block"
                  >
                    Open map link
                  </a>
                ) : null}
              </div>
              <div class="modal-footer">
                <button
                  type="button"
                  class="btn btn-secondary"
                  onClick={() => setSubPickupDetailsModal(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Status Update Modal */}
      {/* Booking activity log */}
      {activityLogModal && (
        <div
          class="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          onClick={closeActivityLog}
        >
          <div
            class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
            onClick={(e) => e.stopPropagation()}
          >
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">
                  Activity log – Booking #{activityLogModal.bookingIdDisplay}
                </h5>
                <button
                  type="button"
                  class="btn-close"
                  aria-label="Close"
                  onClick={closeActivityLog}
                />
              </div>
              <div class="modal-body" style={{ maxHeight: "70vh" }}>
                {activityLogModal.loading && (
                  <p class="text-muted mb-0">Loading…</p>
                )}
                {!activityLogModal.loading && activityLogModal.error && (
                  <p class="text-danger mb-0">{activityLogModal.error}</p>
                )}
                {!activityLogModal.loading &&
                  !activityLogModal.error &&
                  activityLogModal.entries &&
                  activityLogModal.entries.length === 0 && (
                    <p class="text-muted mb-0">No activity recorded yet.</p>
                  )}
                {!activityLogModal.loading &&
                  !activityLogModal.error &&
                  activityLogModal.entries &&
                  activityLogModal.entries.length > 0 && (
                    <ul class="list-group list-group-flush">
                      {activityLogModal.entries.map((entry, entryIdx) => {
                        const id = String(
                          entry.booking_activity_log_id ?? entryIdx
                        );
                        const changes = normalizeActivityChanges(entry.changes);
                        const hasChanges =
                          changes && Object.keys(changes).length > 0;
                        const expanded = !!activityLogExpandedIds[id];
                        return (
                          <li
                            key={id}
                            class="list-group-item px-0 py-3 border-bottom"
                          >
                            <div class="d-flex gap-2 align-items-start">
                              <i
                                class={`bi ${activityLogIconClass(
                                  entry.activity_type
                                )} fs-5 flex-shrink-0 mt-1`}
                                title={activityLogTypeLabel(entry.activity_type)}
                              />
                              <div class="flex-grow-1 min-w-0">
                                <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
                                  <span class="badge bg-light text-dark border">
                                    {activityLogTypeLabel(entry.activity_type)}
                                  </span>
                                  <span class="text-muted small">
                                    {entry.created_on_formatted ||
                                      entry.created_on ||
                                      ""}
                                  </span>
                                  {entry.actor_user_name ? (
                                    <span class="small">
                                      · {entry.actor_user_name}
                                    </span>
                                  ) : null}
                                </div>
                                {entry.summary ? (
                                  <div class="small">{entry.summary}</div>
                                ) : null}
                                {hasChanges && (
                                  <div class="mt-2">
                                    <button
                                      type="button"
                                      class="btn btn-link btn-sm p-0 align-baseline shadow-none"
                                      onClick={() => toggleActivityLogExpand(id)}
                                    >
                                      {expanded ? "Hide details" : "Show field changes"}
                                      <i
                                        class={`bi ms-1 bi-chevron-${expanded ? "up" : "down"}`}
                                      />
                                    </button>
                                    {expanded && (
                                      <div class="table-responsive mt-2">
                                        <table class="table table-sm table-bordered mb-0 small">
                                          <thead>
                                            <tr>
                                              <th>Field</th>
                                              <th>Old</th>
                                              <th>New</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {Object.entries(changes).map(
                                              ([field, pair]) => (
                                                <tr key={field}>
                                                  <td class="text-break">
                                                    {field}
                                                  </td>
                                                  <td class="text-break">
                                                    {renderActivityChangeCell(
                                                      pair,
                                                      "old"
                                                    )}
                                                  </td>
                                                  <td class="text-break">
                                                    {renderActivityChangeCell(
                                                      pair,
                                                      "new"
                                                    )}
                                                  </td>
                                                </tr>
                                              )
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
              </div>
              <div class="modal-footer">
                <button
                  type="button"
                  class="btn btn-secondary"
                  onClick={closeActivityLog}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showFollowupModal && (
        <div 
          class="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabindex="-1"
        >
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Update Follow-up Status</h5>
                <button 
                  type="button" 
                  class="btn-close" 
                  onClick={() => {
                    setShowFollowupModal(false);
                    setSelectedBookingId(null);
                    setSelectedFollowupStatus('');
                    setSelectedBookingInfo(null);
                    setNotifyAgent(true);
                  }}
                ></button>
              </div>
              <div class="modal-body">
                {/* Passenger Information */}
                {selectedBookingInfo && (
                  <div class="mb-4 p-3 bg-light rounded">
                    <h6 class="mb-2">Passenger Information:</h6>
                    <div class="row">
                      <div class="col-md-6">
                        <strong>Name:</strong> {selectedBookingInfo.pax_name}
                      </div>
                      <div class="col-md-6">
                        <strong>Mobile:</strong> {selectedBookingInfo.pax_contact_no}
                      </div>
                      <div class="col-md-6">
                        <strong>Agency:</strong> {selectedBookingInfo.agency_name || 'Not specified'}
                      </div>
                      <div class="col-md-6">
                        <strong>WhatsApp Chat:</strong> {selectedBookingInfo.chat_name || 'Not set'}
                      </div>
                    </div>
                  </div>
                )}
                
                <div class="mb-3">
                  <label class="form-label">Select Follow-up Status:</label>
                  <select 
                    class="form-select"
                    value={selectedFollowupStatus}
                    onChange={(e) => {
                      const selectedStatus = e.target.value;
                      setSelectedFollowupStatus(selectedStatus);
                      
                      // Find the selected status object to get notify_whatsapp field
                      if (selectedStatus && followupStatus) {
                        const statusObj = followupStatus.find(status => status.follow_up_status_id === selectedStatus);
                        if (statusObj) {
                          setNotifyAgent(statusObj.notify_whatsapp === '1');
                        }
                      }
                    }}
                  >
                    <option value="">Choose a status...</option>
                    {followupStatus && followupStatus.map((status) => (
                      <option key={status.follow_up_status_id} value={status.follow_up_status_id}>
                        {status.follow_up_status_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* WhatsApp Notification Checkbox */}
                {selectedBookingInfo && selectedBookingInfo.chat_name && (
                  <div class="mb-3">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="notifyAgentCheckbox"
                        checked={notifyAgent}
                        onChange={(e) => setNotifyAgent(e.target.checked)}
                      />
                      <label class="form-check-label" for="notifyAgentCheckbox">
                        Notify Agent via WhatsApp
                      </label>
                    </div>
                    <small class="text-muted">
                      Send follow-up status update to the agent via WhatsApp
                    </small>
                  </div>
                )}
              </div>
              <div class="modal-footer">
                <button 
                  type="button" 
                  class="btn btn-secondary" 
                  onClick={() => {
                    setShowFollowupModal(false);
                    setSelectedBookingId(null);
                    setSelectedFollowupStatus('');
                    setSelectedBookingInfo(null);
                    setNotifyAgent(true);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  class="btn btn-primary" 
                  onClick={handleFollowupStatusUpdate}
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Bookings;
